import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useI18n } from "@/contexts/I18nContext";
import { useBranding, resolveLogo } from "@/contexts/BrandingContext";
import { PORTAL_T } from "@/lib/portalSubmitI18n";
import { CampaignSlot } from "@/components/portal/CampaignTemplates";
import PortalWelcomeMusic from "@/components/portal/PortalWelcomeMusic";
import PortalValueSection from "@/components/portal/PortalValueSection";
import LandingBackdrop from "@/components/portal/LandingBackdrop";

const PORTAL_API = `${process.env.REACT_APP_BACKEND_URL}/api/portal`;

export default function PortalLanding() {
  const { theme, dir, lang } = useI18n();
  const { branding } = useBranding();
  const T = PORTAL_T[lang];
  const navigate = useNavigate();

  const name = lang === "ar" ? branding.app_name_ar : branding.app_name_en;
  const tagline = lang === "ar" ? branding.tagline_ar : branding.tagline_en;

  const css = makeStyles(theme);

  // Fetch active campaigns on mount + every 60 s so STAS edits reflect on the
  // live portal without forcing the client to refresh the page.
  const [campaigns, setCampaigns] = useState([]);
  useEffect(() => {
    let cancelled = false;
    const load = () => axios.get(`${PORTAL_API}/campaigns`).then(({ data }) => {
      if (!cancelled) setCampaigns(data.items || []);
    }).catch(() => { /* silent — keep last known list */ });
    load();
    const t = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);
  const slotProps = { campaigns, lang, theme, dir };

  return (
    <div dir={dir} style={css.page} data-testid="portal-landing">
      <LandingBackdrop theme={theme} />
      <div style={css.layer}>
        <CampaignSlot placement="landing_top" {...slotProps} />
      </div>
      {/* Spacer reserves room for the passport-opens-visa scene that sits
          in the LandingBackdrop above. The top brand logo is intentionally
          omitted here — the scene itself is the visual hero. */}
      <div style={css.heroSpacer} aria-hidden />

      <div style={css.layer}>
        <CampaignSlot placement="landing_middle" {...slotProps} />
      </div>

      <div style={css.cta}>
        <button onClick={() => navigate("submit")} style={css.btnPrimary} data-testid="landing-start">
          {T.start}
        </button>
        <button onClick={() => navigate("track")} style={css.btnGhost} data-testid="landing-track">
          {T.track}
        </button>
      </div>

      <div style={css.layer}>
        <CampaignSlot placement="landing_bottom" {...slotProps} />
      </div>

      <PortalValueSection theme={theme} dir={dir} lang={lang} />

      <PortalWelcomeMusic theme={theme} />
    </div>
  );
}

const makeStyles = (theme) => ({
  page: {
    minHeight: "calc(100vh - 120px)",
    display: "flex", flexDirection: "column", alignItems: "center",
    justifyContent: "flex-start", padding: "60px 24px 0", textAlign: "center",
    fontFamily: "'Tajawal','Segoe UI',sans-serif",
    color: theme.text, position: "relative", overflow: "hidden",
  },
  logo: { width: "150px", height: "150px", objectFit: "contain", marginBottom: "20px", zIndex: 1, position: "relative" },
  heroSpacer: { width: "100%", height: "200px", pointerEvents: "none" },
  layer: { position: "relative", zIndex: 1, width: "100%", display: "flex", flexDirection: "column", alignItems: "center" },
  title: {
    fontSize: "32px", fontWeight: 900, color: theme.gold,
    margin: "0 0 12px", letterSpacing: "0.5px", lineHeight: 1.3, maxWidth: "720px", zIndex: 1,
  },
  sub: { fontSize: "15px", color: theme.textMuted, marginBottom: "40px", maxWidth: "520px", zIndex: 1 },
  cta: {
    display: "flex", gap: "12px", flexWrap: "wrap", justifyContent: "center",
    width: "100%", maxWidth: "440px", zIndex: 1, position: "relative",
  },
  btnPrimary: {
    flex: "1 1 200px", padding: "16px 24px",
    background: theme.goldGradient, color: theme.name === "dark" ? "#0a0a0a" : "#fff",
    border: "none", borderRadius: "12px", fontSize: "15px", fontWeight: 800,
    cursor: "pointer", letterSpacing: "0.4px",
    boxShadow: theme.name === "dark" ? "0 8px 24px rgba(212,175,55,0.25)" : "0 8px 24px rgba(184,144,42,0.20)",
    transition: "transform 150ms ease",
  },
  btnGhost: {
    flex: "1 1 200px", padding: "16px 24px",
    background: "transparent", color: theme.text,
    border: `1.5px solid ${theme.borderAccent}`, borderRadius: "12px",
    fontSize: "15px", fontWeight: 800, cursor: "pointer", letterSpacing: "0.4px",
  },
  note: { marginTop: "32px", fontSize: "12px", color: theme.textFaint, maxWidth: "420px", zIndex: 1, position: "relative" },
});
